import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "iframe", "status"]
  static values = {
    url: String,
    blockType: String,
    blockId: String
  }

  connect() {
    this.timeout = null
    this.formElement = this.formTarget.querySelector("form") || this.formTarget.closest("form")
    if (!this.formElement) return
    this.bindFormEvents()
    this.fetchPreview()
  }

  disconnect() {
    if (this.timeout) clearTimeout(this.timeout)
    this.unbindFormEvents()
  }

  bindFormEvents() {
    this.handleInput = this.debouncePreview.bind(this)
    this.formElement.addEventListener("input", this.handleInput)
    this.formElement.addEventListener("change", this.handleInput)
  }

  unbindFormEvents() {
    if (!this.formElement) return
    this.formElement.removeEventListener("input", this.handleInput)
    this.formElement.removeEventListener("change", this.handleInput)
  }

  debouncePreview() {
    if (this.timeout) clearTimeout(this.timeout)
    this.setStatus("updating")
    this.timeout = setTimeout(() => this.fetchPreview(), 300)
  }

  async fetchPreview() {
    this.setStatus("updating")

    const formData = new FormData(this.formElement)
    const body = new URLSearchParams()

    for (const [key, value] of formData.entries()) {
      if (key === "_method" || key === "authenticity_token" || key === "commit") continue
      body.append(key, value)
    }

    if (this.hasBlockIdValue && this.blockIdValue) {
      body.append("id", this.blockIdValue)
    }

    if (this.hasBlockTypeValue && this.blockTypeValue) {
      body.append("block_type", this.blockTypeValue)
    }

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json",
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: body.toString()
      })

      if (!response.ok) throw new Error(`Server returned ${response.status}`)

      const data = await response.json()
      this.renderInIframe(data.html || "")
      this.setStatus("ready")
    } catch (error) {
      console.error("Preview error:", error)
      this.setStatus("error")
    }
  }

  renderInIframe(html) {
    const iframe = this.iframeTarget
    const doc = iframe.contentDocument || iframe.contentWindow.document

    const fullHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css">
  <link rel="stylesheet" href="/assets/content-blocks.css">
  <style>
    body {
      padding: 1rem;
      margin: 0;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      font-size: 0.9375rem;
      line-height: 1.6;
      color: #212529;
      background: #fff;
    }
    img { max-width: 100%; height: auto; }
    .content-block--accordion .accordion-item__button { cursor: pointer; }
    .content-block--accordion .accordion-item__content {
      max-height: 500px;
      overflow: hidden;
    }
    .content-block--accordion .accordion-item.is-collapsed .accordion-item__content {
      max-height: 0;
    }
  </style>
</head>
<body>
  ${html || '<p style="color:#999;font-style:italic;">No preview available</p>'}
  <script>
    document.querySelectorAll('.accordion-item__button').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var item = btn.closest('.accordion-item');
        if (item) {
          item.classList.toggle('is-expanded');
          item.classList.toggle('is-collapsed');
        }
      });
    });
  </script>
</body>
</html>`

    doc.open()
    doc.write(fullHtml)
    doc.close()

    // Auto-resize iframe to content height
    setTimeout(() => {
      try {
        const height = doc.documentElement.scrollHeight
        iframe.style.height = Math.max(height, 100) + "px"
      } catch (e) {
        // ignore cross-origin errors
      }
    }, 100)
  }

  setStatus(status) {
    if (!this.hasStatusTarget) return

    const labels = {
      updating: "Updating...",
      ready: "Preview updated",
      error: "Preview error"
    }

    const colors = {
      updating: "text-amber-500",
      ready: "text-green-500",
      error: "text-red-500"
    }

    this.statusTarget.textContent = labels[status] || status
    this.statusTarget.className = `text-xs ${colors[status] || "text-gray-400"}`

    if (status === "ready") {
      if (this.statusTimeout) clearTimeout(this.statusTimeout)
      this.statusTimeout = setTimeout(() => {
        if (this.hasStatusTarget) {
          this.statusTarget.textContent = "Ready"
          this.statusTarget.className = "text-xs text-gray-400"
        }
      }, 2000)
    }
  }

  get csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.content : ""
  }
}
